import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle2, XCircle, Building2 } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { AuthButton } from './AuthButton';

import { bearerAuthHeader } from '../utils/apiHeaders';
export function QuickSubdomainSetup() {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState<'form' | 'complete'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call backend to create subdomain setup
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/quick-subdomain-setup`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
            companyName: companyName.trim(),
            subdomain: subdomain.toLowerCase().trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set up subdomain');
      }

      setSuccess(true);
      setStep('complete');
      setLoading(false);

      // Redirect after 3 seconds
      setTimeout(() => {
        window.location.href = `${window.location.origin}?tenant=${subdomain}`;
      }, 3000);
    } catch (err: any) {
      console.error('Subdomain setup error:', err);
      setError(err.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
            <CardTitle className="text-center">Setup Complete</CardTitle>
            <CardDescription className="text-center">
              Your subdomain has been configured successfully
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>Subdomain:</strong> {subdomain}.pathkit.pro
              </p>
              <p className="text-sm text-green-800 mt-1">
                <strong>Company:</strong> {companyName}
              </p>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Redirecting you to your login page...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="flex justify-end mb-4">
        <AuthButton />
      </div>
      <div className="flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              <CardTitle>Quick Subdomain Setup</CardTitle>
          </div>
          <CardDescription>
            Set up your company subdomain to access PathKit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Your Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500">
                This should be the email you used when you first created your account
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="Acme Corporation"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subdomain">Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                  placeholder="mycompany"
                  disabled={loading}
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  title="Lowercase letters, numbers, and hyphens only"
                />
                <span className="text-gray-600 whitespace-nowrap">.pathkit.pro</span>
              </div>
              <p className="text-xs text-gray-500">
                Choose a unique subdomain (lowercase letters, numbers, and hyphens only)
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !email || !companyName || !subdomain}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Create Subdomain'
              )}
            </Button>

            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                This will create a company and associate your existing account with it.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}